import * as React from "react"
import { CalendarIcon, Clock } from "lucide-react"
import { format } from "date-fns"
import { ptBR } from "date-fns/locale"

import { cn } from "@/lib/utils"
import { Button } from "@/components/ui/button"
import { Calendar } from "@/components/ui/calendar"
import {
    Popover,
    PopoverContent,
    PopoverTrigger,
} from "@/components/ui/popover"
import { Input } from "@/components/ui/input";

interface DateTimePickerProps {
    date: Date | undefined;
    setDate: (date: Date | undefined) => void;
    className?: string;
    placeholder?: string;
}

export function DateTimePicker({
    date,
    setDate,
    className,
    placeholder = "Selecione data e hora"
}: DateTimePickerProps) {
    const [selectedDateTime, setSelectedDateTime] = React.useState<Date | undefined>(date);

    // Sync internal state with prop
    React.useEffect(() => {
        setSelectedDateTime(date);
    }, [date]);

    const handleSelect = (day: Date | undefined) => {
        if (!day) return;

        const newDateTime = new Date(day);
        if (selectedDateTime) {
            newDateTime.setHours(selectedDateTime.getHours());
            newDateTime.setMinutes(selectedDateTime.getMinutes());
        } else {
            // Default to current time if no time previously set
            const now = new Date();
            newDateTime.setHours(now.getHours());
            newDateTime.setMinutes(now.getMinutes());
        }

        setSelectedDateTime(newDateTime);
        setDate(newDateTime);
    };

    const handleTimeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const timeValue = e.target.value;
        if (!timeValue) return;

        const [hours, minutes] = timeValue.split(':').map(Number);
        const newDateTime = selectedDateTime ? new Date(selectedDateTime) : new Date();

        newDateTime.setHours(hours);
        newDateTime.setMinutes(minutes);

        setSelectedDateTime(newDateTime);
        setDate(newDateTime);
    };

    return (
        <Popover>
            <PopoverTrigger asChild>
                <Button
                    variant={"outline"}
                    className={cn(
                        "w-full justify-start text-left font-normal border-gray-300 hover:bg-green-50 hover:text-green-700 hover:border-green-200 transition-colors",
                        !date && "text-muted-foreground",
                        className
                    )}
                >
                    <CalendarIcon className="mr-2 h-4 w-4 text-green-600" />
                    {date ? (
                        <span className="text-gray-900 font-medium">
                            {format(date, "dd/MM/yyyy HH:mm", { locale: ptBR })}
                        </span>
                    ) : (
                        <span>{placeholder}</span>
                    )}
                </Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0 bg-white border-green-100 shadow-xl" align="start">
                <div className="p-3 border-b border-gray-100 bg-green-50/30">
                    <div className="flex items-center gap-2 mb-2">
                        <Clock className="w-4 h-4 text-green-600" />
                        <span className="text-sm font-semibold text-green-800">Horário</span>
                    </div>
                    <Input
                        type="time"
                        value={selectedDateTime ? format(selectedDateTime, 'HH:mm') : ''}
                        onChange={handleTimeChange}
                        className="w-full border-gray-300 focus:ring-green-500 focus:border-green-500 text-green-900 font-medium"
                    />
                </div>
                <Calendar
                    mode="single"
                    selected={selectedDateTime}
                    onSelect={handleSelect}
                    initialFocus
                    locale={ptBR}
                    className="p-3 pointer-events-auto"
                    classNames={{
                        day_selected:
                            "bg-green-600 text-white hover:bg-green-600 focus:bg-green-600 focus:text-white",
                        day_today: "bg-green-100 text-green-900 font-bold",
                        day: "h-9 w-9 p-0 font-normal aria-selected:opacity-100 hover:bg-green-100 hover:text-green-900 rounded-md transition-colors data-[selected]:bg-green-600 data-[selected]:text-white",
                    }}
                />
            </PopoverContent>
        </Popover>
    )
}
